import {
  CategoryInfo,
  GetTablesTaggingStatusResponse,
} from "@/apis/workbook/types";
import { CategoryIndexItem } from "./category-index-section";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { isCategoryTagged } from "./utils";

const FloatingCategoryIndex = ({
  categories,
  activeCategoryId,
  className,
  taggingStatusData,
}: {
  categories: CategoryInfo[];
  activeCategoryId: string | null;
  className?: string;
  taggingStatusData?: GetTablesTaggingStatusResponse;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const targetItemId = activeCategoryId;
  const targetItemIndex = categories.findIndex(
    (category) => category.id === targetItemId,
  );

  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    setIsIdle(false);
    const timer = setTimeout(() => {
      setIsIdle(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [activeCategoryId]);

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (collapsed && containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [collapsed]);

  useEffect(() => {
    if (collapsed) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setCollapsed(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [collapsed]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed top-14 right-4 z-50 flex p-6 justify-center self-stretch bg-white rounded-2xl border border-neutral-200 shadow-2xl max-w-xs overflow-hidden cursor-pointer w-full",
        !collapsed && "overflow-y-auto",
        collapsed && "overflow-y-hidden",
        className,
      )}
      onClick={handleToggle}
      style={{
        maxHeight: collapsed ? "60px" : "min(868px, 100vh - 112px)",
        transition: "all 0.3s ease",
      }}
    >
      <div
        className={cn(
          "flex flex-col items-start gap-1.5 self-stretch w-full h-full",
          collapsed && "pointer-events-none",
        )}
        style={{
          transform: collapsed
            ? `translateY(-${targetItemIndex * 30 + 36}px)`
            : "translateY(0px)",
          transition: "transform 0.3s ease",
        }}
      >
        <div
          className={cn(
            "inline-flex p-0.5 justify-center items-center gap-2 text-neutral-500",
            collapsed && "opacity-0",
            !collapsed && "opacity-100",
            "transition-opacity duration-300 cursor-pointer hover:bg-neutral-100 active:bg-neutral-200 rounded-sm hover:underline underline-offset-2",
          )}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="label-1-normal-medium">목차</span>
        </div>
        {categories.map((category) => (
          <div
            key={category.id}
            className={cn(
              "w-full overflow-hidden transition-colors duration-300 h-6",
              {
                "bg-neutral-200": category.id === targetItemId && !collapsed,
              },
            )}
          >
            <CategoryIndexItem
              key={category.id}
              label={category.category}
              className={cn("transition-opacity duration-500", {
                "opacity-0":
                  collapsed && category.id !== targetItemId && isIdle,
                "opacity-20":
                  collapsed && category.id !== targetItemId && !isIdle,
                "opacity-100": !collapsed,
              })}
              isTagged={isCategoryTagged(category, taggingStatusData)}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const element = document.getElementById(category.id);
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" });
                } else {
                  console.error(`Element with id ${category.id} not found`);
                }
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FloatingCategoryIndex;
